import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { TournamentGame, TournamentTeam, getImageUrl } from '@/services/api'
import { formatEventDateParts } from '@/utils/timezone'
import { useTeamDisplay, deriveTeamCode } from '@/utils/teamDisplay'
import VenueLink from '@/components/VenueLink'
import { sf } from '../../../core/features/sf'
import { useLabels } from '../../../core/labels'
import { fmtDate } from '@/i18n'

interface ScheduleRoundRobinProps {
  games: TournamentGame[]
  teams: TournamentTeam[]
  matchdays?: Array<{
    id: number
    tournament_id: number
    matchday: number
    matchday_date: string | null
    created_at: string
    updated_at: string
  }>
}

export default function ScheduleRoundRobin({ games, teams: _teams, matchdays = [] }: ScheduleRoundRobinProps) {
  const navigate = useNavigate()
  const teamName = useTeamDisplay()
  const t = useLabels()
  const [selectedMatchday, setSelectedMatchday] = useState<number | null>(null)

  // Organize games by matchday
  const gamesByMatchday = useMemo(() => {
    const matchdays: Map<number, TournamentGame[]> = new Map()
    games.forEach((game) => {
      if (!matchdays.has(game.round_number)) {
        matchdays.set(game.round_number, [])
      }
      matchdays.get(game.round_number)!.push(game)
    })
    matchdays.forEach((matchdayGames) => {
      matchdayGames.sort((a, b) => {
        if (a.game_date && b.game_date) {
          const dateCompare = new Date(a.game_date).getTime() - new Date(b.game_date).getTime()
          if (dateCompare !== 0) return dateCompare
        }
        if (a.game_date && !b.game_date) return -1
        if (!a.game_date && b.game_date) return 1
        const locCompare = (a.venue_name || '').localeCompare(b.venue_name || '')
        if (locCompare !== 0) return locCompare
        return a.match_number - b.match_number
      })
    })
    return matchdays
  }, [games])

  const matchdayNumbers = Array.from(gamesByMatchday.keys()).sort((a, b) => a - b)

  // Auto-select first matchday on mount
  useEffect(() => {
    if (selectedMatchday === null && matchdayNumbers.length > 0) {
      setSelectedMatchday(matchdayNumbers[0])
    }
  }, [matchdayNumbers.length])

  const handleGameClick = (gameId: number) => {
    navigate(`/game/${gameId}`)
  }

  const TeamLogo = ({ src, name, size = 'sm' }: { src: string | null; name: string; size?: 'sm' | 'md' }) => {
    const sizeClasses = size === 'sm' ? 'w-6 h-6' : 'w-8 h-8'
    if (src) {
      return <img src={getImageUrl(src) || ''} alt={name} className={`${sizeClasses} object-contain rounded`} />
    }
    return (
      <div
        className={`${sizeClasses} rounded bg-white/10 border border-secondary/30 flex items-center justify-center text-[10px] font-bold text-white/60`}
      >
        {deriveTeamCode(name)}
      </div>
    )
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <span className="px-2 py-0.5 rounded text-xs font-bold bg-green-500/20 text-green-400 border border-green-500/30">
            {t.status({ status }, { variant: 'compact' }).text}
          </span>
        )
      case 'in_progress':
        return (
          <span className="px-2 py-0.5 rounded text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 animate-pulse">
            {t.status({ status }, { variant: 'compact' }).text}
          </span>
        )
      case 'cancelled':
        return (
          <span className="px-2 py-0.5 rounded text-xs font-bold bg-red-500/20 text-red-400 border border-red-500/30">
            {t.status({ status }, { variant: 'compact' }).text}
          </span>
        )
      case 'postponed':
        return (
          <span className="px-2 py-0.5 rounded text-xs font-bold bg-orange-500/20 text-orange-400 border border-orange-500/30">
            {t.status({ status }, { variant: 'compact' }).text}
          </span>
        )
      default:
        return (
          <span className="px-2 py-0.5 rounded text-xs font-bold bg-white/10 text-white/60 border border-secondary/30">
            {t.status({ status }, { variant: 'compact' }).text}
          </span>
        )
    }
  }

  // Must stay above the empty-state early return (rules of hooks, #266).
  const liveGames = useMemo(() => games.filter(g => g.status === 'in_progress'), [games])

  if (games.length === 0) {
    return (
      <div className="bg-bg-secondary/45 backdrop-blur-md border border-secondary/25 rounded-lg p-8 text-center" {...sf('schedule.round-robin')}>
        <h3 className="text-lg font-bold text-white mb-2">{t.ui.schedule.roundRobinEmptyTitle}</h3>
        <p className="text-white/60">{t.ui.schedule.roundRobinEmptyBody}</p>
      </div>
    )
  }

  return (
    <div className="space-y-6" {...sf('schedule.round-robin')}>
      {/* Live Now Section */}
      {liveGames.length > 0 && (
        <div {...sf('schedule.live-now')}>
          <div className="flex items-center gap-3 mb-3">
            <span className="w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            <h3 className="text-sm font-bold text-green-400 uppercase tracking-wider">{t.ui.schedule.liveNowHeading}</h3>
            <div className="flex-1 h-px bg-green-500/20" />
          </div>
          <div className="space-y-3">
            {liveGames.map((game) => {
              const tbd = t.ui.common.tbd
              const home = { name: game.home_team_name || tbd, code: game.home_team_code || tbd, logo: game.home_team_logo }
              const away = { name: game.away_team_name || tbd, code: game.away_team_code || tbd, logo: game.away_team_logo }
              return (
                <div key={`live-${game.id}`} onClick={() => handleGameClick(game.id)}
                  className="bg-green-500/5 border border-green-500/20 rounded-lg overflow-hidden hover:border-green-500/40 cursor-pointer transition-all group p-4">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2 flex-1">
                      <TeamLogo src={home.logo} name={home.name} size="md" />
                      <span className="font-bold text-white text-sm">{teamName({ name: home.name, short_code: home.code }, 'auto')}</span>
                    </div>
                    <div className="text-center px-4">
                      {/* A live game hidden from fans has no score (#1280): the LIVE label alone. */}
                      {game.home_score != null && game.away_score != null && (
                        <div className="text-2xl font-black text-white">{game.home_score} - {game.away_score}</div>
                      )}
                      <span className="px-2 py-0.5 rounded text-xs font-bold bg-yellow-500/20 text-yellow-400 border border-yellow-500/30 animate-pulse">{t.status({ status: 'in_progress' }, { variant: 'compact' }).text}</span>
                    </div>
                    <div className="flex items-center gap-2 flex-1 justify-end">
                      <span className="font-bold text-white text-sm">{teamName({ name: away.name, short_code: away.code }, 'auto')}</span>
                      <TeamLogo src={away.logo} name={away.name} size="md" />
                    </div>
                  </div>
                  {game.venue_name && (
                    <div className="text-center text-white/60 text-xs mt-2">
                      <VenueLink
                        venueId={game.venue_id}
                        venueName={game.venue_name}
                        parentVenueId={game.parent_venue_id}
                        parentVenueName={game.parent_venue_name}
                      />
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        </div>
      )}

      {/* Matchdays View */}
      <div className="space-y-4">
          {/* Matchday selector */}
          <div className="flex gap-2 flex-wrap">
            {matchdayNumbers.map((matchday) => {
              const matchdayGames = gamesByMatchday.get(matchday) || []
              const completedCount = matchdayGames.filter((g) => g.status === 'completed').length
              const matchdayData = matchdays.find(md => md.matchday === matchday)
              const matchdayDate = matchdayData?.matchday_date
              return (
                <button
                  key={matchday}
                  onClick={() => setSelectedMatchday(matchday)}
                  className={`px-3 py-1.5 rounded-lg font-bold text-sm transition-all ${
                    selectedMatchday === matchday
                      ? 'bg-white/10 text-white border border-secondary/50'
                      : 'bg-bg-secondary/45 text-white/50 hover:text-white border border-secondary/25'
                  }`}
                >
                  <div className="flex flex-col items-start">
                    <span>
                      {/* Two text nodes ("Matchday " + n), exactly as before: one joined
                          string shapes a fraction of a pixel differently. */}
                      {`${t.term('roundRobinRound')} `}{matchday}
                      <span className="ml-1.5 text-xs text-white/60">
                        ({completedCount}/{matchdayGames.length})
                      </span>
                    </span>
                    {matchdayDate && (
                      <span className="text-xs text-white/60 mt-0.5">
                        {/* matchday_date is a pure calendar DATE serialized as UTC midnight;
                            render in UTC so the stored day shows verbatim regardless of viewer tz */}
                        {fmtDate(matchdayDate, { month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' })}
                      </span>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {/* Games list */}
          {selectedMatchday !== null && (
            <div className="space-y-3">
              {(gamesByMatchday.get(selectedMatchday) || []).map((game) => {
                const home = {
                  name: game.home_team_name || t.ui.common.tbd,
                  code: game.home_team_code || t.ui.common.tbd,
                  logo: game.home_team_logo,
                }
                const away = {
                  name: game.away_team_name || t.ui.common.tbd,
                  code: game.away_team_code || t.ui.common.tbd,
                  logo: game.away_team_logo,
                }

                return (
                  <div
                    key={game.id}
                    onClick={() => handleGameClick(game.id)}
                    className="bg-bg-secondary/45 backdrop-blur-md border border-secondary/25 rounded-lg overflow-hidden hover:border-primary/30 cursor-pointer transition-all group"
                  >
                    <div className="p-4">
                      {/* Mobile layout */}
                      <div className="sm:hidden">
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2 flex-1">
                            <TeamLogo src={home.logo} name={home.name} size="md" />
                            <span className="font-bold text-white text-sm truncate">{teamName({ name: home.name, short_code: home.code }, 'auto')}</span>
                          </div>
                          <span className={`font-bold text-xl tabular-nums min-w-[2rem] text-center ${
                            game.status === 'completed' && (game.home_score ?? 0) > (game.away_score ?? 0) ? 'text-green-400' : 'text-white'
                          }`}>
                            {game.home_score}
                          </span>
                        </div>
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2 flex-1">
                            <TeamLogo src={away.logo} name={away.name} size="md" />
                            <span className="font-bold text-white text-sm truncate">{teamName({ name: away.name, short_code: away.code }, 'auto')}</span>
                          </div>
                          <span className={`font-bold text-xl tabular-nums min-w-[2rem] text-center ${
                            game.status === 'completed' && (game.away_score ?? 0) > (game.home_score ?? 0) ? 'text-green-400' : 'text-white'
                          }`}>
                            {game.away_score}
                          </span>
                        </div>
                        <div className="mt-3 flex items-center gap-2 flex-wrap">
                          {getStatusBadge(game.status)}
                          {game.game_date && (() => {
                            const { date, time, tzAbbrev, localTime, localTzAbbrev } = formatEventDateParts(game.game_date, game.timezone)
                            return (
                              <span className="text-xs text-white/60">
                                {date} {time}{tzAbbrev ? ` ${tzAbbrev}` : ''}
                                {localTime && (
                                  <span className="text-white/30 text-[10px]"> ({localTime} {localTzAbbrev})</span>
                                )}
                              </span>
                            )
                          })()}
                        </div>
                      </div>

                      {/* Desktop layout */}
                      <div className="hidden sm:block">
                        <div className="flex items-center justify-between gap-4">
                          {/* Home team */}
                          <div className="flex-1 flex items-center justify-end gap-3">
                            <span className="font-bold text-white truncate">{teamName({ name: home.name, short_code: home.code }, 'full')}</span>
                            <TeamLogo src={home.logo} name={home.name} size="md" />
                          </div>

                          {/* Score */}
                          <div className="flex items-center gap-3 min-w-[100px] justify-center">
                            <span className={`font-bold tabular-nums text-xl ${
                              game.status === 'completed' && (game.home_score ?? 0) > (game.away_score ?? 0) ? 'text-green-400' : 'text-white'
                            }`}>
                              {game.home_score}
                            </span>
                            <span className="text-white/60">-</span>
                            <span className={`font-bold tabular-nums text-xl ${
                              game.status === 'completed' && (game.away_score ?? 0) > (game.home_score ?? 0) ? 'text-green-400' : 'text-white'
                            }`}>
                              {game.away_score}
                            </span>
                          </div>

                          {/* Away team */}
                          <div className="flex-1 flex items-center gap-3">
                            <TeamLogo src={away.logo} name={away.name} size="md" />
                            <span className="font-bold text-white truncate">{teamName({ name: away.name, short_code: away.code }, 'full')}</span>
                          </div>
                        </div>

                        {/* Game info */}
                        <div className="mt-3 flex items-center justify-center gap-4 text-sm">
                          {getStatusBadge(game.status)}
                          {game.game_date && (() => {
                            const { date, time, tzAbbrev, localTime, localTzAbbrev } = formatEventDateParts(game.game_date, game.timezone)
                            return (
                              <span className="text-white/60">
                                {date} {time}{tzAbbrev ? ` ${tzAbbrev}` : ''}
                                {localTime && (
                                  <span className="text-white/30 text-xs"> ({localTime} {localTzAbbrev})</span>
                                )}
                              </span>
                            )
                          })()}
                          {game.venue_name && (
                            <span className="text-white/60">
                              • <VenueLink
                                  venueId={game.venue_id}
                                  venueName={game.venue_name}
                                  parentVenueId={game.parent_venue_id}
                                  parentVenueName={game.parent_venue_name}
                                />
                            </span>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Click indicator */}
                    <div className="px-4 py-2 bg-bg-primary/20 border-t border-secondary/20 flex items-center justify-center gap-2 text-xs text-white/60 group-hover:text-primary transition-colors">
                      <span>{t.ui.schedule.gameDetailsLink}</span>
                      <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
        </div>
    </div>
  )
}

